import isEqual from "lodash/isEqual";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useFormState } from "react-final-form";

import {
  ModelMapParameterMapLayerConfiguration,
  ModelMapWidgetConfiguration,
  ParameterTimeSeriesConfiguration,
  ParameterTimeSeriesConfigurationSeriesInner as ParameterTimeSeriesSeries,
  ParametersGeoBoundingBox,
  StaticMapOverlaysOverlayConfiguration,
  Widget,
} from "@/lib/api";
import { ModelMapBaseLayerStyle } from "@/lib/mapbox/base-layer-styles";
import { toMoved } from "@/lib/array";
import { toTimeSeriesParameterOption } from "@/features/shared/widgets/map/time-series/time-series-parameter-option.utils";
import { backfillObservationsMapTimeSeriesConfigurations } from "@/lib/api/observations-map-widget-configuration.utils";
import { usePropertyKindsQuery } from "@/lib/parameters/use-property-kinds-query";
import { canonicalizeWidgetUpdatePayload } from "@/features/widget-forms/dialogs/is-unchanged-widget-update";
import { initialViewportPresets } from "@/features/widget-forms/shared/initial-viewport-presets";

import {
  useModelMapLayersQuery,
  useModelParametersQuery,
  useModelsQuery,
  useStaticMapOverlaysQuery,
} from "./queries";

export const useWeatherModelMapForm = () => {
  const form = useForm();
  const formState = useFormState({
    subscription: { values: true },
  });
  const baselineRef = useRef<unknown>(null);
  const [dirtyBaselineReady, setDirtyBaselineReady] = useState(false);

  const title = formState.values.title as string;
  const setTitle = useCallback(
    (title: string) => {
      form.change("title", title);
    },
    [form],
  );

  const configuration: Partial<ModelMapWidgetConfiguration> =
    (formState.values.configuration as Partial<ModelMapWidgetConfiguration>) ?? {};

  const boundsFromConfig = configuration.initial_viewport_bounds;
  const initialViewportBounds: ParametersGeoBoundingBox =
    boundsFromConfig ?? initialViewportPresets.nl;

  const setInitialViewportBounds = useCallback(
    (bounds: ParametersGeoBoundingBox) => {
      form.change("configuration.initial_viewport_bounds", {
        ...bounds,
        north_latitude: clampLatitude(roundCoord(bounds.north_latitude)),
        south_latitude: clampLatitude(roundCoord(bounds.south_latitude)),
        east_longitude: clampLongitude(roundCoord(bounds.east_longitude)),
        west_longitude: clampLongitude(roundCoord(bounds.west_longitude)),
      });
    },
    [form],
  );

  const updateInitialViewportBoundsField = useCallback(
    (field: keyof Omit<ParametersGeoBoundingBox, "type">, rawValue: unknown) => {
      let parsed = parseCoord(rawValue);
      if (parsed !== undefined) {
        switch (field) {
          case "north_latitude":
          case "south_latitude":
            parsed = clampLatitude(parsed);
            break;
          case "east_longitude":
          case "west_longitude":
            parsed = clampLongitude(parsed);
            break;
        }
        form.change(`configuration.initial_viewport_bounds.${field}`, parsed);
      }
    },
    [form],
  );

  const baseLayerStyle =
    configuration.base_layer_style || ModelMapBaseLayerStyle.Monochrome;

  const setBaseLayerStyle = useCallback(
    (value: string) => {
      form.change("configuration.base_layer_style", value);
    },
    [form],
  );

  useEffect(() => {
    if (!boundsFromConfig) {
      form.change("configuration.initial_viewport_bounds", initialViewportPresets.nl);
    }
  }, [boundsFromConfig, form]);

  useEffect(() => {
    const style = configuration.base_layer_style;
    if (typeof style !== "string" || !style.trim()) {
      form.change("configuration.base_layer_style", ModelMapBaseLayerStyle.Monochrome);
    }
  }, [configuration.base_layer_style, form]);

  const selectedModelId = configuration.model_id;
  const parameterMapLayerConfigurations: ModelMapParameterMapLayerConfiguration[] =
    configuration.parameter_map_layer_configurations ?? [];
  const selectedMapLayerIds = useMemo(
    () => parameterMapLayerConfigurations.map((c) => c.parameter_map_layer_id),
    [parameterMapLayerConfigurations],
  );
  const timeSeriesConfigurations: ParameterTimeSeriesConfiguration[] =
    configuration.parameter_time_series_configurations ?? [];
  const staticOverlayConfigurations: StaticMapOverlaysOverlayConfiguration[] =
    configuration.static_map_overlay_configurations ?? [];
  const selectedStaticOverlayIds = useMemo(
    () => staticOverlayConfigurations.map((c) => c.static_map_overlay_id),
    [staticOverlayConfigurations],
  );
  const mapLayerSettings = {};

  const { data: models } = useModelsQuery();

  const selectedModel = useMemo(
    () => models?.find((model) => model.id === selectedModelId),
    [models, selectedModelId],
  );

  const { data: modelParameters, isPending: parametersLoading } =
    useModelParametersQuery(selectedModelId);

  const { unitFamilies } = usePropertyKindsQuery();

  // Once all programmatic form.change() calls have settled (including the async
  // unit backfill that depends on modelParameters), reset the RFF baseline and
  // capture our own snapshot so hasChanges only reflects real user edits.
  useEffect(() => {
    if (parametersLoading) {
      setDirtyBaselineReady(false);
      return;
    }
    form.reset(form.getState().values);
    // Normalize before storing so client-only fields are stripped and don't
    // produce false positives in the dirty check.
    baselineRef.current = canonicalizeWidgetUpdatePayload(form.getState().values as Widget);
    setDirtyBaselineReady(true);
  }, [parametersLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const normalizedCurrent = canonicalizeWidgetUpdatePayload(formState.values as Widget);
  const hasChanges =
    baselineRef.current !== null && !isEqual(normalizedCurrent, baselineRef.current);

  const parameters = useMemo(
    () => modelParameters?.map(toTimeSeriesParameterOption),
    [modelParameters],
  );

  useEffect(() => {
    if (!modelParameters?.length || timeSeriesConfigurations.length === 0) return;
    const catalog = modelParameters.map((parameter) => ({
      id: parameter.id,
      units: parameter.units,
      propertyKindId: parameter.property_kind_id,
    }));
    const backfilled = backfillObservationsMapTimeSeriesConfigurations(
      timeSeriesConfigurations,
      catalog,
      unitFamilies,
    );
    const changed =
      backfilled.length !== timeSeriesConfigurations.length ||
      backfilled.some(
        (config, index) =>
          config.unit?.code !== timeSeriesConfigurations[index]?.unit?.code,
      );
    if (changed) {
      form.change("configuration.parameter_time_series_configurations", backfilled);
    }
  }, [modelParameters, timeSeriesConfigurations, unitFamilies, form]);

  const { data: mapLayers } = useModelMapLayersQuery(selectedModelId);

  const { data: staticOverlays } = useStaticMapOverlaysQuery();

  const updateSelectedModel = useCallback(
    (id: string) => {
      form.change("configuration.model_id", id);
      form.change("configuration.parameter_time_series_configurations", []);
      form.change("configuration.parameter_map_layer_configurations", []);
    },
    [form],
  );

  // Time Series Configuration actions
  const addTimeSeriesConfig = useCallback(
    (config: ParameterTimeSeriesConfiguration) => {
      form.change("configuration.parameter_time_series_configurations", [
        ...timeSeriesConfigurations,
        config,
      ]);
    },
    [form, timeSeriesConfigurations],
  );

  const removeTimeSeriesConfig = useCallback(
    (configIndex: number) => {
      const updated = timeSeriesConfigurations.filter((_, i) => i !== configIndex);
      form.change("configuration.parameter_time_series_configurations", updated);
    },
    [form, timeSeriesConfigurations],
  );

  const reorderTimeSeriesConfigs = useCallback(
    (fromIndex: number, toIndex: number) => {
      form.change(
        "configuration.parameter_time_series_configurations",
        toMoved(timeSeriesConfigurations, fromIndex, toIndex),
      );
    },
    [form, timeSeriesConfigurations],
  );

  const updateTimeSeriesConfig = useCallback(
    (configIndex: number, config: ParameterTimeSeriesConfiguration) => {
      const updated = [...timeSeriesConfigurations];
      updated[configIndex] = config;
      form.change("configuration.parameter_time_series_configurations", updated);
    },
    [form, timeSeriesConfigurations],
  );

  const addSeriesToConfig = useCallback(
    (configIndex: number, series: ParameterTimeSeriesSeries) => {
      const config = timeSeriesConfigurations[configIndex];
      if (!config) return;
      const existingIds = config.series.map((s) => s.parameter_id);
      if (existingIds.includes(series.parameter_id)) return;
      const updated = [...timeSeriesConfigurations];
      updated[configIndex] = {
        ...config,
        series: [...config.series, series],
      };
      form.change("configuration.parameter_time_series_configurations", updated);
    },
    [form, timeSeriesConfigurations],
  );

  const removeSeriesFromConfig = useCallback(
    (configIndex: number, parameterId: string) => {
      const config = timeSeriesConfigurations[configIndex];
      if (!config) return;
      const updated = [...timeSeriesConfigurations];
      updated[configIndex] = {
        ...config,
        series: config.series.filter((s) => s.parameter_id !== parameterId),
      };
      form.change("configuration.parameter_time_series_configurations", updated);
    },
    [form, timeSeriesConfigurations],
  );

  const reorderSeriesInConfig = useCallback(
    (configIndex: number, fromIndex: number, toIndex: number) => {
      const config = timeSeriesConfigurations[configIndex];
      if (!config) return;
      const updated = [...timeSeriesConfigurations];
      updated[configIndex] = {
        ...config,
        series: toMoved(config.series, fromIndex, toIndex),
      };
      form.change("configuration.parameter_time_series_configurations", updated);
    },
    [form, timeSeriesConfigurations],
  );

  const updateSeriesLabel = useCallback(
    (configIndex: number, parameterId: string, label: string) => {
      const config = timeSeriesConfigurations[configIndex];
      if (!config) return;
      const updated = [...timeSeriesConfigurations];
      updated[configIndex] = {
        ...config,
        series: config.series.map((s) =>
          s.parameter_id === parameterId ? { ...s, label } : s,
        ),
      };
      form.change("configuration.parameter_time_series_configurations", updated);
    },
    [form, timeSeriesConfigurations],
  );

  // Map Layer actions
  const addMapLayer = useCallback(
    (details: ModelMapParameterMapLayerConfiguration) => {
      if (selectedMapLayerIds.includes(details.parameter_map_layer_id)) return;
      form.change("configuration.parameter_map_layer_configurations", [
        ...parameterMapLayerConfigurations,
        details,
      ]);
    },
    [form, parameterMapLayerConfigurations, selectedMapLayerIds, mapLayers],
  );

  const removeMapLayer = useCallback(
    (mapLayerId: string) => {
      if (!selectedMapLayerIds.includes(mapLayerId)) return;
      const newConfigs = parameterMapLayerConfigurations.filter(
        (c) => c.parameter_map_layer_id !== mapLayerId,
      );
      form.change("configuration.parameter_map_layer_configurations", newConfigs);
    },
    [form, parameterMapLayerConfigurations, selectedMapLayerIds],
  );

  const reorderMapLayers = useCallback(
    (fromIndex: number, toIndex: number) => {
      form.change(
        "configuration.parameter_map_layer_configurations",
        toMoved(parameterMapLayerConfigurations, fromIndex, toIndex),
      );
    },
    [form, parameterMapLayerConfigurations],
  );

  const renameMapLayer = useCallback(
    (mapLayerId: string, name: string) => {
      name = name.trim();
      // Set to empty string reverts label to initial value
      if (!name) {
        name = mapLayers?.find((it) => it.id === mapLayerId)?.label ?? "(naamloos)";
      }
      const index = parameterMapLayerConfigurations.findIndex(
        (it) => it.parameter_map_layer_id === mapLayerId,
      );
      if (index >= 0) {
        form.change(
          `configuration.parameter_map_layer_configurations[${index}].label`,
          name,
        );
      }
    },
    [form, parameterMapLayerConfigurations, mapLayers],
  );

  const addStaticOverlay = useCallback(
    (details: StaticMapOverlaysOverlayConfiguration) => {
      if (selectedStaticOverlayIds.includes(details.static_map_overlay_id)) return;
      form.change("configuration.static_map_overlay_configurations", [
        ...staticOverlayConfigurations,
        details,
      ]);
    },
    [form, staticOverlayConfigurations, selectedStaticOverlayIds],
  );

  const removeStaticOverlay = useCallback(
    (staticOverlayId: string) => {
      if (!selectedStaticOverlayIds.includes(staticOverlayId)) return;
      form.change(
        "configuration.static_map_overlay_configurations",
        staticOverlayConfigurations.filter(
          (c) => c.static_map_overlay_id !== staticOverlayId,
        ),
      );
    },
    [form, staticOverlayConfigurations, selectedStaticOverlayIds],
  );

  const reorderStaticOverlays = useCallback(
    (fromIndex: number, toIndex: number) => {
      form.change(
        "configuration.static_map_overlay_configurations",
        toMoved(staticOverlayConfigurations, fromIndex, toIndex),
      );
    },
    [form, staticOverlayConfigurations],
  );

  const renameStaticOverlay = useCallback(
    (staticOverlayId: string, name: string) => {
      name = name.trim();
      if (!name) {
        name = staticOverlays?.find((it) => it.id === staticOverlayId)?.name ?? "(unnamed)";
      }
      const index = staticOverlayConfigurations.findIndex(
        (it) => it.static_map_overlay_id === staticOverlayId,
      );
      if (index >= 0) {
        form.change(
          `configuration.static_map_overlay_configurations[${index}].label`,
          name,
        );
      }
    },
    [form, staticOverlayConfigurations, staticOverlays],
  );

  const updateStaticOverlayColor = useCallback(
    (staticOverlayId: string, color: string) => {
      const index = staticOverlayConfigurations.findIndex(
        (it) => it.static_map_overlay_id === staticOverlayId,
      );
      if (index >= 0) {
        form.change(
          `configuration.static_map_overlay_configurations[${index}].color`,
          color,
        );
      }
    },
    [form, staticOverlayConfigurations],
  );

  const updateStaticOverlayEnabledByDefault = useCallback(
    (staticOverlayId: string, enabled: boolean) => {
      const index = staticOverlayConfigurations.findIndex(
        (it) => it.static_map_overlay_id === staticOverlayId,
      );
      if (index >= 0) {
        form.change(
          `configuration.static_map_overlay_configurations[${index}].enabled_by_default`,
          enabled,
        );
      }
    },
    [form, staticOverlayConfigurations],
  );

  return {
    title,
    setTitle,
    hasChanges,
    dirtyBaselineReady,
    // State
    selectedModelId,
    selectedMapLayerIds,
    selectedMapLayers: parameterMapLayerConfigurations,
    selectedStaticOverlayIds,
    staticOverlayConfigurations,
    timeSeriesConfigurations,
    mapLayerSettings,
    initialViewportBounds,
    setInitialViewportBounds,
    updateInitialViewportBoundsField,
    baseLayerStyle,
    setBaseLayerStyle,
    // Data
    models,
    parameters,
    parametersLoading,
    mapLayers,
    staticOverlays,
    selectedModel,
    // Time Series Config Actions
    addTimeSeriesConfig,
    removeTimeSeriesConfig,
    reorderTimeSeriesConfigs,
    updateTimeSeriesConfig,
    addSeriesToConfig,
    removeSeriesFromConfig,
    reorderSeriesInConfig,
    updateSeriesLabel,
    // Map Layer Actions
    updateSelectedModel,
    addMapLayer,
    removeMapLayer,
    renameMapLayer,
    reorderMapLayers,
    addStaticOverlay,
    removeStaticOverlay,
    reorderStaticOverlays,
    renameStaticOverlay,
    updateStaticOverlayColor,
    updateStaticOverlayEnabledByDefault,
    // Form instance (for submit, etc.)
    form,
    configuration,
  };
};

function roundCoord(n: number): number {
  return Math.round(n * 1_000) / 1_000;
}

function parseCoord(v: unknown): number | undefined {
  if (v === "" || v === undefined || v === null) return undefined;
  const n = Number(v);
  return n === n ? n : undefined;
}

function clamp(min: number, max: number, value: number): number {
  return Math.max(min, Math.min(max, value));
}

function clampLatitude(value: number): number {
  return clamp(-90, 90, value);
}
function clampLongitude(value: number): number {
  return clamp(-180, 180, value);
}
